import { useMemo, useState } from "react";
import { applicationsApi, ApiError } from "@/api";
import type { JobApplication, DrawingSet } from "@/types";
import { Button, SelectField, TextField } from "@/components/ui";
import { humanise, drawingCode, workstreamTypeLabel, drawingStatusLabel } from "@/lib/jobs";
import { FormDrawer } from "./FormDrawer";

/** Edit an in-progress application's reference + drawing link (Addendum 2) +
 *  council-portal link. The reference often isn't known at creation (you raise
 *  the application when you submit it, and the LPA/BCB only issues the number
 *  once it's validated), so it's editable here. The drawing link (drawing_id) is
 *  what lets the matching drawing's submit task close when the application is
 *  submitted. The council-portal link (application_view_url) is the deep link
 *  surfaced as "View on council portal" on the card — usually cached by the
 *  PlanWire lookup, but editable here so you can add or correct it by hand. Other
 *  fields remain create-time only. */
export function EditApplicationDrawer({
  app,
  jobNumber,
  drawingSets,
  onClose,
  onSaved,
}: {
  app: JobApplication;
  jobNumber: number;
  drawingSets: DrawingSet[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [appReference, setAppReference] = useState(app.app_reference ?? "");
  const [drawingId, setDrawingId] = useState(app.drawing_id ?? 0);
  const [viewUrl, setViewUrl] = useState(app.application_view_url ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Candidates = drawings of this application's type (other drawings have no
  // application, so they can never match its submit task).
  const matchingDrawings = useMemo(() => {
    const out: { id: number; label: string }[] = [];
    for (const set of drawingSets) {
      for (const d of set.drawings) {
        if (d.drawing_type !== app.type) continue;
        const code = drawingCode(jobNumber, set.set_number, d.drawing_number);
        out.push({
          id: d.id,
          label: `${code} · ${workstreamTypeLabel(d.drawing_type)}${d.title ? ` · ${d.title}` : ""} · ${drawingStatusLabel(d.status)}`,
        });
      }
    }
    return out;
  }, [drawingSets, app.type, jobNumber]);

  async function submit() {
    if (saving) return;
    const refTrimmed = appReference.trim();
    const urlTrimmed = viewUrl.trim();
    const refChanged = refTrimmed !== (app.app_reference ?? "");
    const drawingChanged = (app.drawing_id ?? 0) !== drawingId;
    const urlChanged = urlTrimmed !== (app.application_view_url ?? "");
    if (!refChanged && !drawingChanged && !urlChanged) {
      onClose();
      return;
    }
    // Guard the portal link before any write (mirrors the server): a non-empty
    // value is rendered as an href, so it must be a full http(s):// URL. Blank
    // clears it.
    if (urlChanged && urlTrimmed !== "" && !/^https?:\/\//i.test(urlTrimmed)) {
      setError("The council portal link must be a full http(s):// URL.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      // Reference first: if it's rejected (e.g. a duplicate), surface that before
      // touching the other links, so none of the writes land on a bad reference.
      if (refChanged) await applicationsApi.setApplicationReference(app.id, refTrimmed);
      if (drawingChanged) await applicationsApi.setApplicationDrawing(app.id, drawingId);
      if (urlChanged) await applicationsApi.setApplicationViewUrl(app.id, urlTrimmed);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the application.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="Edit application"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Save</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <p className="job-drawer__muted">{humanise(app.application_type || app.type)}</p>

        <TextField
          label="Application reference"
          value={appReference}
          onChange={(e) => setAppReference(e.target.value)}
          placeholder="e.g. 24/01234/HOU"
          hint="The LPA/BCB reference. Add or correct it here once the application is validated and the number is issued."
        />

        <TextField
          label="Council portal link"
          type="url"
          inputMode="url"
          value={viewUrl}
          onChange={(e) => setViewUrl(e.target.value)}
          onClear={() => setViewUrl("")}
          placeholder="https://pa.example.gov.uk/online-applications/…"
          hint="Deep link to this application on the council/BCB portal — shown as “View on council portal” on the card. Usually filled by the PlanWire lookup; add or correct it here. Clear it (✕) to remove the link."
        />

        {matchingDrawings.length > 0 ? (
          <SelectField
            label="Linked drawing (submit task)"
            value={String(drawingId)}
            onChange={(e) => setDrawingId(Number(e.target.value))}
            hint="Link the drawing this application is for, so submitting it closes that drawing's submit task."
          >
            <option value="0">— none —</option>
            {matchingDrawings.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </SelectField>
        ) : (
          <p className="job-drawer__muted">
            No {app.type === "building_control" ? "building control" : "planning"} drawings on this job yet — create one in the Drawing sets tab.
          </p>
        )}
      </section>
    </FormDrawer>
  );
}
